import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { getEquipments, getProfile, updateProfile } from '@/api/services/workout'
import type { Equipment, ProfileRead } from '@/api/types/workout'
import { Header } from '@/components/shared/header'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type Props = { onClose: () => void }

export const ProfileSettings = ({ onClose }: Props) => {
  const [profile, setProfile] = useState<ProfileRead | null>(null)
  const [allEquipments, setAllEquipments] = useState<Equipment[]>([])
  const [loading, setLoading] = useState(true)
  const { isSubmitting: saving, run } = useSubmitLock()

  // ローカルフォーム
  const [daysPerWeek, setDaysPerWeek] = useState<number | ''>('')
  const [duration, setDuration] = useState<number | ''>('')
  const [selectedEquipments, setSelectedEquipments] = useState<string[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [p, eqs] = await Promise.all([getProfile(), getEquipments()])
      setProfile(p)
      setAllEquipments(eqs)
      setDaysPerWeek(p.training_days_per_week ?? '')
      setDuration(p.session_duration_minutes ?? '')
      setSelectedEquipments(p.available_equipments)
    } catch (err) {
      console.error(err)
      toast.error('プロフィールの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleSave = async () => {
    await run(async () => {
      try {
        const updated = await updateProfile({
          training_days_per_week: daysPerWeek === '' ? null : daysPerWeek,
          session_duration_minutes: duration === '' ? null : duration,
          available_equipments: selectedEquipments,
        })
        setProfile(updated)
        toast.success('プロフィールを保存しました')
      } catch (err) {
        console.error(err)
        toast.error('保存に失敗しました')
      }
    })
  }

  const toggleEquipment = (name: string) => {
    setSelectedEquipments((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]))
  }

  if (loading) {
    return (
      <div className="flex flex-col h-screen">
        <Header title="トレーニング設定" onClose={onClose} />
        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">読み込み中...</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen">
      <Header title="トレーニング設定" onClose={onClose} />
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="space-y-2">
          <Label>週のトレーニング日数</Label>
          <Input
            type="number"
            min={1}
            max={7}
            value={daysPerWeek}
            placeholder="例: 3"
            onChange={(e) => setDaysPerWeek(e.target.value ? Number(e.target.value) : '')}
          />
          <p className="text-xs text-muted-foreground">1〜7日</p>
        </div>

        <div className="space-y-2">
          <Label>1セッションの目標時間（分）</Label>
          <Input
            type="number"
            min={10}
            max={300}
            value={duration}
            placeholder="例: 60"
            onChange={(e) => setDuration(e.target.value ? Number(e.target.value) : '')}
          />
          <p className="text-xs text-muted-foreground">10〜300分</p>
        </div>

        <div className="space-y-2">
          <Label>利用可能な器具</Label>
          <div className="flex flex-wrap gap-3 mt-1">
            {allEquipments.map((eq) => (
              <label key={eq.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                <Checkbox
                  checked={selectedEquipments.includes(eq.name)}
                  onCheckedChange={() => toggleEquipment(eq.name)}
                />
                {eq.name}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 border-t">
        <Button onClick={handleSave} disabled={saving || !profile} className="w-full">
          {saving ? '保存中...' : '保存'}
        </Button>
      </div>
    </div>
  )
}
